import questionCapabilityManifest from "../../../../config/questionnaire-question-capabilities.json";
import ratingScaleManifest from "../../../../config/questionnaire-rating-scales.json";
import { CONTINUOUS_UNITS, DURATION_EXPOSED_UNITS } from "./vocabularies";
import type { DurationUnit } from "./vocabularies";
import type {
  CanonicalSlideType,
  ContinuousResponse,
  ContinuousUnitId,
  MetricDisplaySpec,
  MetricExtractionSpec,
  QuestionType,
  RatingAnchors,
  RatingUnitId,
  TestedAssetType,
} from "./index";

export const QUESTION_TYPE_IDS = [
  "single_select",
  "multi_select",
  "continuous",
  "rating",
  "open_end",
  "matrix_rating",
  "matrix_categorical",
  "ranking",
] as const satisfies ReadonlyArray<QuestionType>;

export const RATING_UNIT_IDS = [
  "rating_1_5",
  "rating_1_7",
  "rating_1_10",
  "rating_0_10",
  "rating_0_100",
  "binary_yes_no",
] as const satisfies ReadonlyArray<RatingUnitId>;

/** Which point of a rating scale is the good outcome (Python `RatingPolarity`). */
export const RATING_POLARITIES = [
  "higher_is_better",
  "lower_is_better",
  "middle_is_best",
] as const satisfies ReadonlyArray<NonNullable<RatingAnchors["polarity"]>>;
type _MissingRatingPolarities = Exclude<
  NonNullable<RatingAnchors["polarity"]>,
  (typeof RATING_POLARITIES)[number]
>;
const _RATING_POLARITIES_EXHAUSTIVE: [_MissingRatingPolarities] extends [never]
  ? true
  : `RatingPolarity missing from RATING_POLARITIES: ${_MissingRatingPolarities & string}` =
  true;
void _RATING_POLARITIES_EXHAUSTIVE;

/** In `config/questionnaire-vocabularies.json` order. */
export const CONTINUOUS_UNIT_IDS: ReadonlyArray<ContinuousUnitId> = CONTINUOUS_UNITS;

// The duration units live in `config/questionnaire-vocabularies.json`.
export { DURATION_EXPOSED_UNITS };

export const DEFAULT_CURRENCY_CODE = "USD" as const;

/**
 * Canonical, ordered list of tested-asset type ids — the single runtime source of
 * truth for "which asset types exist". The `satisfies` clause proves every id is a
 * valid `TestedAssetType` (array ⊆ union).
 */
export const TESTED_ASSET_TYPE_IDS = [
  "concept",
  "ad",
  "landing_page",
  "product",
  "packaging",
  "message",
  "brand",
  "pain",
] as const satisfies ReadonlyArray<TestedAssetType>;

/**
 * The currencies a product's typed price may carry: the ones the builder's
 * `formatProductPrice` and the report's `format_product_price`
 * (`canonical_deck/slides/helpers.py`) spell alike, the same number included
 * (F18 cross review: HKD, TWD and VND print differently there, and KWD prints
 * a different number). The tested-asset price contract
 * (`productPriceStructureIssues`) admits exactly these, so every writer does;
 * the builder's currency field offers them. The golden fixture
 * `product-version-labels.golden.json` has a `currency_<code>` case for each,
 * and tests hold this list, that fixture and Python's
 * `models.questionnaire.contracts.PRODUCT_PRICE_CURRENCIES` equal.
 */
export const PRODUCT_PRICE_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "MXN",
  "BRL",
  "JPY",
  "CNY",
  "KRW",
  "INR",
  "ILS",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "ZAR",
] as const;

/**
 * Compile-time drift guard (union ⊆ array). `satisfies` above only proves the
 * forward direction; this proves the reverse: every `TestedAssetType` member must
 * appear in `TESTED_ASSET_TYPE_IDS`. If a member is added to the union without a
 * matching entry here, `_MissingTestedAssetTypeIds` resolves to that member and the
 * assignment below fails to compile (the error names the missing id). Tuple-wrapping
 * (`[T] extends [never]`) stops the conditional from distributing over a union, so
 * the check stays correct when several ids are missing at once.
 */
type _MissingTestedAssetTypeIds = Exclude<
  TestedAssetType,
  (typeof TESTED_ASSET_TYPE_IDS)[number]
>;
const _TESTED_ASSET_TYPE_IDS_EXHAUSTIVE: [_MissingTestedAssetTypeIds] extends [never]
  ? true
  : `TestedAssetType missing from TESTED_ASSET_TYPE_IDS: ${_MissingTestedAssetTypeIds & string}` =
  true;
void _TESTED_ASSET_TYPE_IDS_EXHAUSTIVE;

export type LpComposite = "comprehension" | "conversion_intent" | "desirability";

export interface LpCompositeDimension {
  /** Composite-membership tag stamped on the question (lp: namespaced). */
  tag: string;
  /** Which of the three landing-page composites this dimension feeds. */
  composite: LpComposite;
  /** Raw weight; the T1 engine renormalizes per present component. */
  weight: number;
  /** Objection-style dimensions invert before weighting. */
  direction: "higher_is_better" | "lower_is_better";
  /** "rating" = matrix-rating measure; "open_end" = T4 classifier. */
  contributes: "rating" | "open_end";
  /** T4 classifier kind for open_end dimensions, otherwise null. */
  kind: string | null;
  /** True when the dimension only applies if the page has that element. */
  conditional: boolean;
}

/**
 * Shared landing-page composite-dimension contract (T2).
 *
 * Single source of truth for the three landing-page composites. Each landing-page
 * dimension question is tagged with its composite membership; the T5 composite
 * builder groups tagged questions by `composite` into one CompositeMetricDefinition
 * per composite. The T1 engine renormalizes the raw `weight` values, so they need
 * not sum to 1 here.
 *
 * MUST stay byte-identical (the `tag` strings especially) with
 * LP_COMPOSITE_DIMENSIONS in apps/python-api/models/questionnaire/registry.py.
 * The drift guard lives in
 * tests/apps/python-api/test_questionnaire_contract_models.py.
 */
export const LP_COMPOSITE_DIMENSIONS = [
  {
    tag: "lp:comprehension",
    composite: "comprehension",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: false,
  },
  {
    tag: "lp:comprehension_value_prop",
    composite: "comprehension",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "open_end",
    kind: "value_prop_understood",
    conditional: false,
  },
  {
    tag: "lp:comprehension_message_match",
    composite: "comprehension",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: true,
  },
  {
    tag: "lp:conversion_intent",
    composite: "conversion_intent",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: false,
  },
  {
    tag: "lp:conversion_cta_clarity",
    composite: "conversion_intent",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: false,
  },
  {
    tag: "lp:conversion_price_value",
    composite: "conversion_intent",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: true,
  },
  {
    tag: "lp:conversion_objection",
    composite: "conversion_intent",
    weight: 1.0,
    direction: "lower_is_better",
    contributes: "open_end",
    kind: "objection",
    conditional: false,
  },
  {
    tag: "lp:desirability",
    composite: "desirability",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: false,
  },
  {
    tag: "lp:desirability_trust",
    composite: "desirability",
    weight: 1.0,
    direction: "higher_is_better",
    contributes: "rating",
    kind: null,
    conditional: false,
  },
] as const satisfies ReadonlyArray<LpCompositeDimension>;

/**
 * The registered landing-page topic of each `lp:` tag: the composite it feeds.
 * Mirrors `LP_TOPIC_BY_TAG` in apps/python-api/models/questionnaire/registry.py.
 */
const LP_TOPIC_BY_TAG: ReadonlyMap<string, LpComposite> = new Map(
  LP_COMPOSITE_DIMENSIONS.map((dimension) => [dimension.tag, dimension.composite]),
);

/**
 * The landing-page topic a question's registered `lp:` tags declare, or null
 * when it carries none or its tags name two different topics. Mirrors
 * `lp_topic_of_tags` in apps/python-api/models/questionnaire/registry.py.
 */
export function lpTopicOfTags(tags: unknown): LpComposite | null {
  if (!Array.isArray(tags)) {
    return null;
  }
  const topics = new Set<LpComposite>();
  for (const tag of tags) {
    if (typeof tag === "string") {
      const topic = LP_TOPIC_BY_TAG.get(tag);
      if (topic) {
        topics.add(topic);
      }
    }
  }
  if (topics.size !== 1) {
    return null;
  }
  return [...topics][0];
}

/**
 * True when any question carries a registered `lp:` tag: the stored-shape
 * classification the LP topic readers choose on. Mirrors `declares_lp_topics`.
 */
export function declaresLpTopics(
  questions: ReadonlyArray<{ tags?: ReadonlyArray<string> | null }>,
): boolean {
  return questions.some((question) =>
    (question.tags ?? []).some((tag) => LP_TOPIC_BY_TAG.has(tag)),
  );
}

/** Every canonical landing-page dimension tag (derived from the contract above). */
export type LpCompositeDimensionTag =
  (typeof LP_COMPOSITE_DIMENSIONS)[number]["tag"];

/**
 * Canonical, presentation-ready label for each landing-page composite dimension.
 *
 * Single source of truth for the short metric names shown in the report
 * scorecard and deck. Names follow the concept/landing-page testing vocabulary
 * used by leading platforms (e.g. Zappi/Qualtrics: Clarity, Message Match,
 * Conversion Intent, Trust) so the output reads like an industry scorecard
 * instead of the raw survey question text.
 *
 * The builder stamps these onto `MetricDisplaySpec.shortLabel` keyed by the
 * scored question's `lp:` dimension tag. The `satisfies` clause guarantees the
 * map stays exhaustive as dimensions are added or renamed.
 */
export const LP_DIMENSION_DISPLAY_LABELS = {
  "lp:comprehension": "Clarity",
  "lp:comprehension_value_prop": "Value Prop",
  "lp:comprehension_message_match": "Message Match",
  "lp:conversion_intent": "Conversion Intent",
  "lp:conversion_cta_clarity": "CTA Clarity",
  "lp:conversion_price_value": "Price–Value",
  "lp:conversion_objection": "Objection Handling",
  "lp:desirability": "Desirability",
  "lp:desirability_trust": "Trust",
} as const satisfies Record<LpCompositeDimensionTag, string>;

/**
 * Paired-lift extraction contract (A6).
 *
 * A within-subject pre/post design asks the SAME measure twice — once before
 * the stimulus is revealed and once after — and reports how far the measure
 * MOVED for each individual respondent. A metric declares it by setting
 * `extraction.method` to {@link PAIRED_LIFT_METHOD} and naming the PRE question
 * in `MetricDefinition.baselineQuestionId`; the metric's own `questionId` is the
 * POST half.
 *
 * The instrument-side pairing tag (`lift:pre:<measure>` / `lift:post:<measure>`,
 * see LIFT_TAG_PREFIX in the backend questionnaire runtime) is what the builder
 * pairs halves on; `baselineQuestionId` is the resolved result of that pairing,
 * frozen into the metric plan so analysis never has to re-derive it.
 *
 * Wording rule, binding on every consumer: paired lift measures the stimulus
 * MOVING a measure. It does not measure persuasion — the design cannot separate
 * persuasion from brand-name salience.
 *
 * MUST stay in sync with `_extract_paired_lift` in
 * apps/python-api/canonical_analysis/runtime.py, which produces the breakdown
 * keys enumerated here.
 */
export const PAIRED_LIFT_METHOD = "paired_lift" as const satisfies MetricExtractionSpec["method"];

/**
 * Breakdown-key prefixes reserved by paired lift. Every one starts with `__`,
 * which is the established "not a row label" marker in `MetricResult.breakdown`
 * — report and deck consumers already skip `__`-prefixed keys, so these never
 * render as chart rows.
 */
export const PAIRED_LIFT_BREAKDOWN_PREFIXES = [
  "__pre:",
  "__post:",
  "__pairs:",
  "__lift:",
  "__movers:",
  "__test:",
] as const;

/**
 * The named breakdown entries a consumer may read by key. Everything else under
 * `__pre:` / `__post:` is the standard rating secondary-metric breakdown for
 * that half (`__pre:bucket:4`, `__post:favorable_share`, ...).
 */
export const PAIRED_LIFT_BREAKDOWN_KEYS = {
  /** Mean of the PRE half over the paired base. */
  preMean: "__pre:mean",
  /** Respondents who answered the PRE half at all (paired or not). */
  preRespondents: "__pre:n",
  /** Mean of the POST half over the paired base. */
  postMean: "__post:mean",
  /** Respondents who answered the POST half at all (paired or not). */
  postRespondents: "__post:n",
  /** Respondents present on BOTH halves — this is `MetricResult.baseSize`. */
  pairs: "__pairs:n",
  /** Respondents present on exactly one half, dropped by the inner join. */
  pairsLost: "__pairs:lost",
  /** `pairs / (pairs + pairsLost)` as a percentage. */
  pairsCoveragePercent: "__pairs:coverage_percent",
  /** Mean paired difference (post − pre). Mirrors `MetricResult.nativeValue`. */
  liftMean: "__lift:mean",
  /** Median paired difference. */
  liftMedian: "__lift:median",
  /** Paired respondents whose answer moved up / down / not at all. */
  moversUp: "__movers:up",
  moversDown: "__movers:down",
  moversUnchanged: "__movers:unchanged",
  /** Wilcoxon signed-rank statistic `min(W+, W−)`. */
  testStatistic: "__test:statistic",
  /** Normal-approximation z for the statistic. */
  testZ: "__test:z",
  /** Two-sided p-value. */
  testPValue: "__test:p_value",
  /** Matched-pairs rank-biserial correlation, signed, in [−1, 1]. */
  testEffectR: "__test:effect_r",
  /** Pairs entering the test (equals `pairs`; Pratt keeps zero differences). */
  testN: "__test:n",
} as const;

export interface PairedLiftContract {
  method: typeof PAIRED_LIFT_METHOD;
  /** The single test run on the paired differences. */
  test: "wilcoxon_signed_rank";
  /**
   * How zero differences are handled. `pratt` ranks them and then excludes
   * their ranks from the statistic while KEEPING them in the base and in the
   * variance term. The scipy default (`wilcox`) discards them outright, which
   * silently reduces N to the movers — and in a twin panel most respondents do
   * not move, so the default would report a test on a small, self-selected
   * subgroup while the base still read as the full paired count.
   */
  zeroMethod: "pratt";
  /** Two-sided: the design does not pre-declare a direction. */
  alternative: "two_sided";
  /**
   * No multiplicity correction in v1: one pre-declared primary measure and
   * descriptive-only secondaries means there is no family to correct.
   * Secondary measures must therefore never grow significance markers.
   */
  multiplicity: "none";
  /** Below this many pairs the normal approximation is not trustworthy. */
  minPairsForTest: number;
  reservedBreakdownPrefixes: typeof PAIRED_LIFT_BREAKDOWN_PREFIXES;
}

export const PAIRED_LIFT_CONTRACT = {
  method: PAIRED_LIFT_METHOD,
  test: "wilcoxon_signed_rank",
  zeroMethod: "pratt",
  alternative: "two_sided",
  multiplicity: "none",
  minPairsForTest: 10,
  reservedBreakdownPrefixes: PAIRED_LIFT_BREAKDOWN_PREFIXES,
} as const satisfies PairedLiftContract;

/** True for a breakdown key produced by the paired-lift extraction. */
export function isPairedLiftBreakdownKey(key: string): boolean {
  return PAIRED_LIFT_BREAKDOWN_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export type SurveyJsTarget =
  | "radiogroup"
  | "checkbox"
  | "text_numeric"
  | "rating"
  | "comment"
  | "matrix"
  | "matrixdropdown"
  | "ranking";

/**
 * Study systems that may use a question type. The registry is shared across both
 * systems and mode-gated: regular studies filter to `regular`, Quick Study filters
 * to `quick_study`. A type may serve both. Gating keeps the unification additive —
 * neither system sees a type it does not opt into.
 */
export type QuestionTypeMode = "regular" | "quick_study";

/**
 * SurveyJS matrix cell shapes (the `cellType` on a `matrixdropdown` column). The
 * full SurveyJS set is modeled so the contract is complete, but only the cells in
 * active use are wired into the builder/preview today (`rating` for emotion/score
 * measures, `text` for PSM price cues); the rest are declared-but-unbuilt and added
 * on first use.
 */
export type MatrixCellType =
  | "dropdown"
  | "checkbox"
  | "radiogroup"
  | "text"
  | "comment"
  | "boolean"
  | "rating";

/**
 * Per-type content contract — what authored content a type requires. Build,
 * preview, and shape-enforcement all read this one source instead of re-encoding
 * the rules. Layer 3 of the form/content split (Layer 1 = `surveyJsTarget`,
 * Layer 2 = `cellType`, Layer 3 = this content shape).
 */
export interface QuestionTypeContentContract {
  /** Needs a list of answer options (single/multi-select, ranking). */
  requiresChoices: boolean;
  /** Needs measure columns (matrix family). */
  requiresColumns: boolean;
  /** Needs a bounded rating scale/unit (rating, matrix_rating). */
  requiresScale: boolean;
  /** Rows are the tested stimuli/statements (matrix family). */
  rowsAreStimuli: boolean;
}

const CONTENT_CONTRACT_FLAGS = [
  "requiresChoices",
  "requiresColumns",
  "requiresScale",
  "rowsAreStimuli",
] as const satisfies ReadonlyArray<keyof QuestionTypeContentContract>;

/**
 * Each type's content contract is `content` in
 * `config/questionnaire-question-capabilities.json` (`questionTypes.<type>`);
 * Python's `models/questionnaire/registry.py` reads the same key.
 */
const QUESTION_TYPE_CAPABILITIES = questionCapabilityManifest.questionTypes;
if (
  QUESTION_TYPE_IDS.some((type) => {
    const content: Record<string, unknown> | undefined =
      QUESTION_TYPE_CAPABILITIES[type]?.content;
    return (
      content == null ||
      JSON.stringify(Object.keys(content).sort()) !==
        JSON.stringify([...CONTENT_CONTRACT_FLAGS].sort()) ||
      CONTENT_CONTRACT_FLAGS.some((flag) => typeof content[flag] !== "boolean")
    );
  })
) {
  throw new TypeError("Question-type content contracts are malformed.");
}

/**
 * What a one-line summary of this type's result may carry. `scored`: the engine's
 * deterministic metric line is a score and the modal answer is a real share of the
 * panel. `themes`: open answers coded into themes; no score. `none`: no single-number
 * summary (the engine may still score per option). Read by the workflow twin-test
 * lane to decide what evidence a report writer sees; never a routing or rendering
 * decision elsewhere.
 */
export type QuestionSummaryMeasure = "scored" | "themes" | "none";

/**
 * The analyses a question type reports by default, as descriptive tags. Not
 * `MetricKind` values: "winner_option" or "themes" name an
 * analysis, not the statistic a metric definition computes. Mirror of
 * `QuestionTypeAnalysisTag` in `models/questionnaire/registry.py`.
 */
export type QuestionTypeAnalysisTag =
  | "distribution"
  | "winner_option"
  | "share"
  | "option_shares"
  | "top_options"
  | "co_selection"
  | "unit_specific_average"
  | "spread"
  | "unit_specific_mean"
  | "secondary_metrics"
  | "themes"
  | "quotes"
  | "keywords"
  | "mean_per_row_measure"
  | "distribution_per_row_category"
  | "ranking_weighted_score";

export interface QuestionTypeRegistryEntry {
  id: QuestionType;
  respondentIntent: string;
  surveyJsTarget: SurveyJsTarget;
  /** Which study systems may use this type (mode-gating; see {@link QuestionTypeMode}). */
  modes: Array<QuestionTypeMode>;
  /** Matrix cell shape (matrixdropdown family only); omitted for non-matrix types. */
  cellType?: MatrixCellType;
  /** Authored-content requirements (see {@link QuestionTypeContentContract}). */
  content: QuestionTypeContentContract;
  defaultAnalysisTags: Array<QuestionTypeAnalysisTag>;
  summaryMeasure: QuestionSummaryMeasure;
  defaultSlideTypes: Array<CanonicalSlideType>;
}

export type UnitFamily = "rating" | "continuous";

export type NativeDisplayFormat = MetricDisplaySpec["valueFormat"];

export interface NormalizationRecipe {
  method: "linear_bounds" | "binary" | "explicit_bounds_required";
  min?: number;
  max?: number;
  formula?: string;
}

export interface UnitRegistryEntry {
  id: RatingUnitId | ContinuousUnitId;
  family: UnitFamily;
  label: string;
  rawMin?: number;
  rawMax?: number;
  nativeDisplay: NativeDisplayFormat;
  primaryMetric: MetricExtractionSpec["method"];
  secondaryMetrics: Array<string>;
  normalization: NormalizationRecipe;
  requiresExplicitBoundsForComposite: boolean;
  defaultCurrencyCode?: typeof DEFAULT_CURRENCY_CODE;
  durationUnits?: Array<DurationUnit>;
}

export const QUESTION_TYPE_REGISTRY = {
  single_select: {
    id: "single_select",
    respondentIntent: "Choose one option",
    surveyJsTarget: "radiogroup",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.single_select.content,
    defaultAnalysisTags: ["distribution", "winner_option", "share"],
    summaryMeasure: "scored",
    defaultSlideTypes: ["single_select_choice_bars"],
  },
  multi_select: {
    id: "multi_select",
    respondentIntent: "Choose multiple options",
    surveyJsTarget: "checkbox",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.multi_select.content,
    defaultAnalysisTags: ["option_shares", "top_options", "co_selection"],
    summaryMeasure: "none",
    defaultSlideTypes: ["multi_select_choice_bars"],
  },
  continuous: {
    id: "continuous",
    respondentIntent: "Enter numeric value",
    surveyJsTarget: "text_numeric",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.continuous.content,
    defaultAnalysisTags: ["unit_specific_average", "spread"],
    summaryMeasure: "none",
    defaultSlideTypes: ["continuous_density"],
  },
  rating: {
    id: "rating",
    respondentIntent: "Score one object or statement",
    surveyJsTarget: "rating",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.rating.content,
    defaultAnalysisTags: ["unit_specific_mean", "secondary_metrics"],
    summaryMeasure: "scored",
    defaultSlideTypes: ["rating_likert"],
  },
  open_end: {
    id: "open_end",
    respondentIntent: "Free text response",
    surveyJsTarget: "comment",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.open_end.content,
    defaultAnalysisTags: ["themes", "quotes", "keywords"],
    summaryMeasure: "themes",
    defaultSlideTypes: ["qual_positive_themes", "qual_negative_themes"],
  },
  matrix_rating: {
    id: "matrix_rating",
    respondentIntent: "Score rows across one or more measures",
    surveyJsTarget: "matrixdropdown",
    modes: ["regular", "quick_study"],
    cellType: "rating",
    content: QUESTION_TYPE_CAPABILITIES.matrix_rating.content,
    defaultAnalysisTags: ["mean_per_row_measure", "secondary_metrics"],
    summaryMeasure: "none",
    defaultSlideTypes: ["matrix_rating"],
  },
  matrix_categorical: {
    id: "matrix_categorical",
    respondentIntent: "Assign categories to rows",
    surveyJsTarget: "matrixdropdown",
    modes: ["regular", "quick_study"],
    cellType: "dropdown",
    content: QUESTION_TYPE_CAPABILITIES.matrix_categorical.content,
    defaultAnalysisTags: ["distribution_per_row_category"],
    summaryMeasure: "none",
    defaultSlideTypes: ["matrix_categorical_bars"],
  },
  ranking: {
    id: "ranking",
    respondentIntent: "Order items by preference",
    surveyJsTarget: "ranking",
    modes: ["regular", "quick_study"],
    content: QUESTION_TYPE_CAPABILITIES.ranking.content,
    defaultAnalysisTags: ["ranking_weighted_score"],
    summaryMeasure: "none",
    defaultSlideTypes: ["ranking_weighted"],
  },
} as const satisfies Record<QuestionType, QuestionTypeRegistryEntry>;

/**
 * `config/questionnaire-rating-scales.json`, the one owner of every stored
 * rating unit's bounds and normalization recipe (`units`, in `RatingUnitId`
 * order) and of the supported scales (`scales`, read by `rating-scales.ts`).
 * Python reads the same file (`apps/python-api/models/questionnaire/rating_scales.py`).
 */
export const RATING_SCALE_MANIFEST = ratingScaleManifest;

const RATING_UNIT_SCALES: Readonly<
  Record<RatingUnitId, { min: number; max: number; normalization: string; formula: string }>
> = ratingScaleManifest.units;

if (
  ratingScaleManifest.schemaVersion !== "questionnaire-rating-scales.v2" ||
  Object.keys(RATING_UNIT_SCALES).join() !== RATING_UNIT_IDS.join() ||
  RATING_UNIT_IDS.some(
    (unit) =>
      !["linear_bounds", "binary"].includes(RATING_UNIT_SCALES[unit].normalization) ||
      !(RATING_UNIT_SCALES[unit].min < RATING_UNIT_SCALES[unit].max),
  )
) {
  throw new TypeError(
    "Questionnaire rating-scale registry units do not match RatingUnitId.",
  );
}

/** A rating unit's raw bounds, from the one owner. */
function ratingUnitRawBounds(unit: RatingUnitId): { rawMin: number; rawMax: number } {
  const { min, max } = RATING_UNIT_SCALES[unit];
  return { rawMin: min, rawMax: max };
}

/** A rating unit's normalization recipe, from the one owner. */
function ratingUnitNormalization(unit: RatingUnitId): NormalizationRecipe {
  const { min, max, normalization, formula } = RATING_UNIT_SCALES[unit];
  return {
    method: normalization as NormalizationRecipe["method"],
    min,
    max,
    formula,
  };
}

export const RATING_UNIT_REGISTRY = {
  rating_1_5: {
    id: "rating_1_5",
    family: "rating",
    label: "1-5 rating",
    ...ratingUnitRawBounds("rating_1_5"),
    nativeDisplay: "mean_out_of_scale",
    primaryMetric: "mean",
    secondaryMetrics: ["favorable_share_4_5", "top_box_5", "normalized_mean"],
    normalization: ratingUnitNormalization("rating_1_5"),
    requiresExplicitBoundsForComposite: false,
  },
  rating_1_7: {
    id: "rating_1_7",
    family: "rating",
    label: "1-7 rating",
    ...ratingUnitRawBounds("rating_1_7"),
    nativeDisplay: "mean_out_of_scale",
    primaryMetric: "mean",
    secondaryMetrics: ["favorable_share_6_7", "top_box_7", "normalized_mean"],
    normalization: ratingUnitNormalization("rating_1_7"),
    requiresExplicitBoundsForComposite: false,
  },
  rating_1_10: {
    id: "rating_1_10",
    family: "rating",
    label: "1-10 rating",
    ...ratingUnitRawBounds("rating_1_10"),
    nativeDisplay: "mean_out_of_scale",
    primaryMetric: "mean",
    secondaryMetrics: ["favorable_share_8_10", "top_box_10", "normalized_mean"],
    normalization: ratingUnitNormalization("rating_1_10"),
    requiresExplicitBoundsForComposite: false,
  },
  rating_0_10: {
    id: "rating_0_10",
    family: "rating",
    label: "0-10 rating",
    ...ratingUnitRawBounds("rating_0_10"),
    nativeDisplay: "mean_out_of_scale",
    primaryMetric: "mean",
    secondaryMetrics: ["promoter_share_9_10", "detractor_share_0_6", "nps_like", "normalized_mean"],
    normalization: ratingUnitNormalization("rating_0_10"),
    requiresExplicitBoundsForComposite: false,
  },
  rating_0_100: {
    id: "rating_0_100",
    family: "rating",
    label: "0-100 rating",
    ...ratingUnitRawBounds("rating_0_100"),
    nativeDisplay: "index",
    primaryMetric: "mean",
    secondaryMetrics: ["favorable_share_70_100", "top_box_90_100", "normalized_mean"],
    normalization: ratingUnitNormalization("rating_0_100"),
    requiresExplicitBoundsForComposite: false,
  },
  binary_yes_no: {
    id: "binary_yes_no",
    family: "rating",
    label: "Binary yes/no",
    ...ratingUnitRawBounds("binary_yes_no"),
    nativeDisplay: "percent",
    primaryMetric: "mean",
    secondaryMetrics: ["no_share", "net_yes"],
    normalization: ratingUnitNormalization("binary_yes_no"),
    requiresExplicitBoundsForComposite: false,
  },
} as const satisfies Record<RatingUnitId, UnitRegistryEntry>;

export const CONTINUOUS_UNIT_REGISTRY = {
  percent_0_100: {
    id: "percent_0_100",
    family: "continuous",
    label: "Percent 0-100",
    rawMin: 0,
    rawMax: 100,
    nativeDisplay: "percent",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "distribution_bands"],
    normalization: { method: "linear_bounds", min: 0, max: 100, formula: "value / 100" },
    requiresExplicitBoundsForComposite: false,
  },
  currency: {
    id: "currency",
    family: "continuous",
    label: "Currency",
    nativeDisplay: "currency",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "range", "distribution_bands"],
    normalization: { method: "explicit_bounds_required" },
    requiresExplicitBoundsForComposite: true,
    defaultCurrencyCode: DEFAULT_CURRENCY_CODE,
  },
  count: {
    id: "count",
    family: "continuous",
    label: "Count",
    nativeDisplay: "count",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "range", "distribution_bands"],
    normalization: { method: "explicit_bounds_required" },
    requiresExplicitBoundsForComposite: true,
  },
  duration: {
    id: "duration",
    family: "continuous",
    label: "Duration",
    nativeDisplay: "duration",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "range", "distribution_bands"],
    normalization: { method: "explicit_bounds_required" },
    requiresExplicitBoundsForComposite: true,
    durationUnits: [...DURATION_EXPOSED_UNITS],
  },
  probability_0_1: {
    id: "probability_0_1",
    family: "continuous",
    label: "Probability 0-1",
    rawMin: 0,
    rawMax: 1,
    nativeDisplay: "probability",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "probability_bands"],
    normalization: { method: "linear_bounds", min: 0, max: 1, formula: "value" },
    requiresExplicitBoundsForComposite: false,
  },
  index_0_100: {
    id: "index_0_100",
    family: "continuous",
    label: "Index 0-100",
    rawMin: 0,
    rawMax: 100,
    nativeDisplay: "index",
    primaryMetric: "continuous_summary",
    secondaryMetrics: ["median", "favorable_index_share"],
    normalization: { method: "linear_bounds", min: 0, max: 100, formula: "value / 100" },
    requiresExplicitBoundsForComposite: false,
  },
} as const satisfies Record<ContinuousUnitId, UnitRegistryEntry>;

export const UNIT_REGISTRY = {
  ...RATING_UNIT_REGISTRY,
  ...CONTINUOUS_UNIT_REGISTRY,
} as const satisfies Record<RatingUnitId | ContinuousUnitId, UnitRegistryEntry>;

export function isQuestionType(value: string): value is QuestionType {
  return QUESTION_TYPE_IDS.includes(value as QuestionType);
}

export function isRatingUnitId(value: string): value is RatingUnitId {
  return RATING_UNIT_IDS.includes(value as RatingUnitId);
}

export function isContinuousUnitId(value: string): value is ContinuousUnitId {
  return CONTINUOUS_UNIT_IDS.includes(value as ContinuousUnitId);
}

export function isUnitId(value: string): value is RatingUnitId | ContinuousUnitId {
  return isRatingUnitId(value) || isContinuousUnitId(value);
}

/**
 * Canonical question-type ids available to a given study system (mode-gating).
 * Regular studies pass `"regular"`; Quick Study passes `"quick_study"`. Order
 * follows {@link QUESTION_TYPE_IDS} so callers get a stable, deterministic list.
 */
export function questionTypeIdsForMode(mode: QuestionTypeMode): Array<QuestionType> {
  return QUESTION_TYPE_IDS.filter((id) => QUESTION_TYPE_REGISTRY[id].modes.includes(mode));
}

export function getQuestionTypeRegistryEntry(value: string): QuestionTypeRegistryEntry | undefined {
  if (!isQuestionType(value)) {
    return undefined;
  }

  return QUESTION_TYPE_REGISTRY[value];
}

export function getUnitRegistryEntry(value: string): UnitRegistryEntry | undefined {
  if (!isUnitId(value)) {
    return undefined;
  }

  return UNIT_REGISTRY[value];
}

/**
 * Inclusive [min, max] scale bounds for a rating unit (authored unit,
 * pre-coercion), read from `config/questionnaire-rating-scales.json`. The one
 * reader every TypeScript consumer uses (the compiler, the runtime validator,
 * the builder and the report). A unit this registry does not know is refused,
 * never given a default scale.
 */
export function ratingBounds(unit: RatingUnitId): [number, number] {
  const entry: UnitRegistryEntry | undefined = isRatingUnitId(unit)
    ? RATING_UNIT_REGISTRY[unit]
    : undefined;
  if (typeof entry?.rawMin !== "number" || typeof entry.rawMax !== "number") {
    throw new TypeError(`Rating unit ${String(unit)} has no registered bounds.`);
  }
  return [entry.rawMin, entry.rawMax];
}

/**
 * Whether a rating unit has a middle point: an odd number of points between
 * its registered bounds. rating_1_5, rating_1_7, rating_0_10 and rating_0_100
 * have one; rating_1_10, binary_yes_no and a unit this registry does not know
 * have none (C04-1b). Python's `rating_has_midpoint` reads the same bounds.
 *
 * derivation: derived_rating_midpoint.
 */
export function ratingHasMidpoint(unit: unknown): boolean {
  const entry: UnitRegistryEntry | undefined =
    typeof unit === "string" && isRatingUnitId(unit)
      ? RATING_UNIT_REGISTRY[unit]
      : undefined;
  if (typeof entry?.rawMin !== "number" || typeof entry.rawMax !== "number") {
    return false;
  }
  return (entry.rawMax - entry.rawMin + 1) % 2 === 1;
}

/**
 * A scale's midpoint label on `unit`: the label when the unit has a midpoint,
 * otherwise undefined. Every writer that authors a scale sets
 * `anchors.midpointLabel` through this, and every reader of a stored one reads
 * it through this; a pass-through carries what it is given.
 *
 * derivation: derived_rating_midpoint.
 */
export function ratingMidpointLabel(
  unit: unknown,
  label: string | null | undefined,
): string | undefined {
  return ratingHasMidpoint(unit) && label != null ? label : undefined;
}

export function getEffectiveCurrencyCode(response: Pick<ContinuousResponse, "currencyCode">): string {
  return response.currencyCode ?? DEFAULT_CURRENCY_CODE;
}

export function unitRequiresExplicitBoundsForComposite(unitId: string): boolean {
  return getUnitRegistryEntry(unitId)?.requiresExplicitBoundsForComposite ?? false;
}
